import { Box, SxProps } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface PdfViewerHandle {
  // Opens the browser's print dialog for the currently displayed PDF.
  print(): void;
}

export const PdfViewer = forwardRef<PdfViewerHandle, { pdf: Blob; sx?: SxProps }>(function PdfViewer(props, ref) {
  const { pdf, sx } = props;
  const [url, setUrl] = useState<string>();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const url = URL.createObjectURL(pdf);
    setUrl(`${url}#toolbar=0`);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pdf]);

  useImperativeHandle(ref, () => ({
    print() {
      // Safari cannot reliably print a PDF that is embedded in an <iframe>.
      // `contentWindow.print()` often prints the parent page instead of the PDF, or does nothing at all.
      // As a workaround we open the PDF in a new tab, so the user can print it via Safari's own PDF viewer (⌘P).
      if (_isSafari()) {
        if (url) {
          window.open(url, "_blank", "noopener");
        }
        return;
      }
      const printWindow = iframeRef.current?.contentWindow;
      if (!printWindow) {
        console.error("PdfViewer.print: iframe.contentWindow is null");
        return;
      }
      printWindow.focus();
      printWindow.print();
    },
  }));

  return (
    <Box sx={sx}>
      <iframe
        ref={iframeRef}
        data-testid="PdfViewer"
        src={url ?? "about:blank"}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </Box>
  );
});

// Detects Safari (both macOS Safari and any browser on iOS, which are all forced to use WebKit).
// We rely on `navigator.vendor === "Apple Computer, Inc."`, which is set by WebKit-based browsers and not by Chrome, Firefox or Edge on macOS.
function _isSafari(): boolean {
  return typeof navigator !== "undefined" && navigator.vendor === "Apple Computer, Inc.";
}
